import React from "react";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { Video } from "./types";
import {
  AddVideoTolist,
  getVideoList,
  RemoveVideoFromList,
} from "../../services/ContentServices";
import {
  Box,
  Drawer,
  IconButton,
  InputAdornment,
  TablePagination,
  TextField,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import SearchIcon from "@mui/icons-material/Search";
import { TableBodyVideoList } from "../../utils/ContentCardList";
import { useSnackbar } from "notistack";

interface VideoPlayListDrawerProps {
  open: boolean;
  onClose: () => void;
  storedVidIds?: string[];
  selectedPlayList?: string;
  onDataUpdate?: () => void;
}
const VideoPlayListDrawer: React.FC<VideoPlayListDrawerProps> = ({
  open,
  onClose,
  onDataUpdate,
  storedVidIds,
  selectedPlayList,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalElements, setTotalElements] = useState<number>(0);
  const rowsPerPageOptions = [5, 15, 25];
  const [videos, setVideos] = useState<Video[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageOptions[0]);
  const [page, setPage] = useState<number>(0);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) {
      getVideos();
    }
  }, [page, rowsPerPage, open]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const getVideos = async () => {
    setLoading(true);
    try {
      const response = await getVideoList({ page, size: rowsPerPage });
      setVideos(response.data.content);
      setTotalElements(response.data.totalElements);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddToPlaylist = async (id: string): Promise<number> => {
    const isPresent = storedVidIds !== undefined && storedVidIds.includes(id);

    const status = isPresent
      ? await RemoveVideoFromList({
          playlistId: selectedPlayList as string,
          videoId: id,
        })
      : await AddVideoTolist({
          playlistId: selectedPlayList as string,
          videoId: id,
        });

    if (status === 200) {
      enqueueSnackbar(
        isPresent
          ? "Successfully! Video has been removed"
          : "Successfully! Video has been added",
        {
          variant: "success",
        }
      );
      return 200;
    }

    enqueueSnackbar(
      isPresent
        ? "Failed! Video could not be removed"
        : "Failed! Video could not be added",
      {
        variant: "error",
      }
    );
    return 401;
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            backgroundColor: theme.palette.background.paper,
            borderTopLeftRadius: theme.spacing(2),
            borderBottomLeftRadius: theme.spacing(2),
            width: 450,
            height: "100vh",
            boxSizing: "border-box",
          },
        }}
      >
        <Box
          component="form"
          aria-label="Video List"
          sx={{
            display: "flex",
            backgroundColor: theme.palette.background.paper,
            flexDirection: "column",
            height: "100%",
            padding: theme.spacing(1),
            boxSizing: "border-box",
          }}
        >
          <Box
            mb={2}
            mt={2}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                marginRight: theme.spacing(1),
              }}
            >
              <KeyboardBackspaceIcon />
            </IconButton>
            <Box
              sx={{
                width: "75%",
              }}
            >
              <TextField
                placeholder="Search by name"
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              marginBottom: theme.spacing(1),
              scrollbarWidth: "none",
            }}
          >
            <TableBodyVideoList
              drawer
              loading={loading}
              videos={videos}
              refreshList={onDataUpdate}
              readOnly={storedVidIds}
              callBackchange={handleAddToPlaylist}
            />
          </Box>
          <Box sx={{ marginTop: "auto" }}>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={totalElements}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default VideoPlayListDrawer;
